/**
 * Safe redirect utilities to prevent open redirect vulnerabilities.
 * All redirects should be validated to ensure they are internal paths only.
 */

/**
 * Validates that a path is safe for internal redirect.
 * Prevents open redirect attacks by rejecting external URLs and protocol handlers.
 */
export const isInternalPath = (path: string): boolean => {
  if (!path || typeof path !== 'string') return false;
  
  // Only allow paths starting with /
  if (!path.startsWith('/')) return false;
  
  // Prevent protocol handlers (javascript:, data:, etc.)
  if (path.includes(':')) return false;
  
  // Prevent double slashes that could be interpreted as protocol-relative URLs
  if (path.startsWith('//')) return false;
  
  // Prevent encoded characters that could bypass validation
  // Check for URL-encoded colons or slashes
  if (path.includes('%3A') || path.includes('%3a')) return false;
  if (path.includes('%2F%2F') || path.includes('%2f%2f')) return false;
  
  // Prevent backslash (Windows-style path that could be reinterpreted)
  if (path.includes('\\')) return false;
  
  return true;
};

/**
 * Returns a safe redirect path, falling back to default if the path is invalid.
 */
export const getSafeRedirectPath = (path: string | null | undefined, defaultPath: string = '/dashboard'): string => {
  if (!path) return defaultPath;
  
  return isInternalPath(path) ? path : defaultPath;
};

/**
 * Extracts and validates a redirect parameter from URL search params.
 */
export const getRedirectFromUrl = (searchParams: URLSearchParams, defaultPath: string = '/dashboard'): string => {
  const redirect = searchParams.get('redirect');
  return getSafeRedirectPath(redirect, defaultPath);
};

/**
 * Builds a safe auth redirect URL with validated redirect parameter.
 */
export const buildAuthRedirectUrl = (currentPath: string, sessionExpired: boolean = false): string => {
  const safePath = getSafeRedirectPath(currentPath, '/dashboard');
  const baseUrl = '/auth';
  
  if (sessionExpired) {
    return `${baseUrl}?session_expired=true&redirect=${encodeURIComponent(safePath)}`;
  }
  
  return `${baseUrl}?redirect=${encodeURIComponent(safePath)}`;
};
